import React from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import { ChannelToggles, describePushResult } from '../../components/ChannelToggles';
import {
  BookOpen, Edit, Trash2, Plus, FileText, Heart, Mail, Send, Users, Video, Upload,
  Quote, Check, HelpCircle, Lightbulb, ChevronUp, MessageCircle, Eye, Clock, Sparkles,
  Link2, Key, Copy, AlertTriangle,
} from 'lucide-react';

const btnStyle = (bg, color) => ({ fontFamily: 'Nunito, sans-serif', backgroundColor: bg, color, fontWeight: 600 });

const copyArticleLink = async (articleId) => {
  const url = `${window.location.origin}/article/${articleId}`;
  try {
    await navigator.clipboard.writeText(url);
    toast.success('Link copied — ready to paste anywhere.', { description: url });
  } catch {
    toast.error(`Copy failed — the link is: ${url}`);
  }
};

export function OverviewTab({ pendingConsultations, quickActions = [], alertsPanel = null }) {
  return (
    <div className="space-y-6">
      {alertsPanel}

      {quickActions.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((qa) => (
              <button
                key={qa.key}
                onClick={qa.onClick}
                className="group relative p-5 rounded-2xl text-left overflow-hidden transition-all duration-300 hover:-translate-y-1"
                style={{ backgroundColor: '#FFFFFF', boxShadow: '0 6px 24px rgba(0,0,0,0.05)', border: '1px solid #EFEAE0' }}
                data-testid={`quick-action-${qa.key}`}
              >
                <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full opacity-15 transition-transform duration-500 group-hover:scale-125" style={{ backgroundColor: qa.tint }} />
                <div className="relative">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${qa.tint}20` }}>
                    <qa.Icon className="w-5 h-5" style={{ color: qa.tint }} />
                  </div>
                  <p className="font-semibold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29', fontSize: '15px' }}>
                    {qa.label}
                  </p>
                  <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{qa.hint}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="p-8 rounded-3xl relative overflow-hidden" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}>
        <svg className="absolute -bottom-4 -right-4 opacity-[0.06] pointer-events-none" width="220" height="120" viewBox="0 0 220 120" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M0 90 L40 60 L70 80 L110 40 L150 70 L190 30 L220 55 L220 120 L0 120 Z" fill="#5B7C99" />
          <path d="M0 100 L50 80 L90 95 L140 65 L180 90 L220 70 L220 120 L0 120 Z" fill="#78A18E" />
        </svg>
        <div className="relative">
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
            Pending Consultations ({pendingConsultations.length})
          </h2>
          {pendingConsultations.length > 0 ? (
            <div className="space-y-4">
              {pendingConsultations.map((c) => (
                <Link key={c.id} to={`/consultation/${c.id}`} className="block p-4 rounded-2xl transition-all duration-300 hover:-translate-y-1" style={{ backgroundColor: '#FDFBF7' }}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{c.title}</h3>
                      <p className="text-sm mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>{c.description.slice(0, 120)}...</p>
                      <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{c.user_name} • {c.tier} tier • {new Date(c.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className="px-4 py-2 rounded-full text-sm font-semibold" style={{ backgroundColor: '#E8C26520', color: '#E8C265', fontFamily: 'Nunito, sans-serif' }}>Respond</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>No pending consultations — you're all caught up. 🌿</p>
          )}
        </div>
      </div>
    </div>
  );
}

export function ArticlesTab({ articles, categories, openArticleForm, deleteArticle, openNotifyModal }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Your Articles</h2>
        <button onClick={() => openArticleForm()} className="flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 hover:-translate-y-1" style={btnStyle('#E57A44', '#FDFBF7')} data-testid="new-article-btn">
          <Plus className="w-5 h-5" /><span>New Article</span>
        </button>
      </div>
      <div className="space-y-4">
        {articles.map((article) => (
          <div key={article.id} className="p-6 rounded-2xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)' }}>
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: '#E8C26520', color: '#E8C265', fontFamily: 'Nunito, sans-serif' }}>
                    {categories.find(c => c.id === article.category)?.name || article.category}
                  </span>
                  {article.status === 'draft' && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: '#5B7C9920', color: '#5B7C99', fontFamily: 'Nunito, sans-serif' }}>
                      <FileText className="w-3 h-3" /><span>Draft</span>
                    </span>
                  )}
                  {article.status === 'scheduled' && article.scheduled_for && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: '#B77BB020', color: '#B77BB0', fontFamily: 'Nunito, sans-serif' }} data-testid={`scheduled-badge-${article.id}`} title={new Date(article.scheduled_for).toLocaleString()}>
                      <Clock className="w-3 h-3" /><span>Scheduled · {new Date(article.scheduled_for).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                    </span>
                  )}
                  {article.status === 'published' && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: '#78A18E15', color: '#78A18E', fontFamily: 'Nunito, sans-serif' }} data-testid={`views-${article.id}`}>
                      <Eye className="w-3 h-3" /><span>{article.view_count || 0} view{(article.view_count || 0) === 1 ? '' : 's'}</span>
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{article.title}</h3>
                <div className="text-sm line-clamp-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content.replace(/<[^>]+>/g, ' ').slice(0, 150) + '...') }}></div>
              </div>
              <div className="flex gap-2">
                {article.status === 'published' && (
                  <>
                    <button onClick={() => copyArticleLink(article.id)} className="p-2 rounded-full transition-all duration-300 hover:scale-110" style={{ backgroundColor: '#78A18E20' }} title="Copy shareable link" aria-label="Copy shareable link" data-testid={`copy-link-${article.id}`}>
                      <Link2 className="w-4 h-4" style={{ color: '#78A18E' }} />
                    </button>
                    <button onClick={() => openNotifyModal(article.id)} className="p-2 rounded-full transition-all duration-300 hover:scale-110" style={{ backgroundColor: '#E57A4420' }} title="Notify subscribers" aria-label="Notify subscribers" data-testid={`notify-article-${article.id}`}>
                      <Send className="w-4 h-4" style={{ color: '#E57A44' }} />
                    </button>
                  </>
                )}
                <button onClick={() => openArticleForm(article)} className="p-2 rounded-full transition-all duration-300 hover:scale-110" style={{ backgroundColor: '#5B7C9920' }} data-testid={`edit-article-${article.id}`}>
                  <Edit className="w-4 h-4" style={{ color: '#5B7C99' }} />
                </button>
                <button onClick={() => deleteArticle(article.id)} className="p-2 rounded-full transition-all duration-300 hover:scale-110" style={{ backgroundColor: '#DC262620' }} data-testid={`delete-article-${article.id}`}>
                  <Trash2 className="w-4 h-4" style={{ color: '#DC2626' }} />
                </button>
              </div>
            </div>
          </div>
        ))}
        {articles.length === 0 && (
          <p className="text-center py-12" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>No articles yet. Click "New Article" to write your first one.</p>
        )}
      </div>
    </div>
  );
}

export function CategoriesTab({ categories, openCategoryForm }) {
  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Category Customization</h2>
        <p className="text-sm mt-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Customize the name, description, icon and featured verse for each category shown on your homepage</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {categories.map((cat) => (
          <div key={cat.id} className="p-6 rounded-2xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)' }}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-xl font-semibold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{cat.name}</h3>
                <p className="text-sm mt-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>{cat.description}</p>
              </div>
              <button onClick={() => openCategoryForm(cat)} className="p-2 rounded-full transition-all duration-300 hover:scale-110" style={{ backgroundColor: '#5B7C9920' }} data-testid={`edit-category-${cat.id}`}>
                <Edit className="w-4 h-4" style={{ color: '#5B7C99' }} />
              </button>
            </div>
            {cat.verse_text && (
              <div className="mt-3 p-3 rounded-xl italic" style={{ backgroundColor: '#F3EFE7', fontFamily: 'Lora, serif', color: '#595553', fontSize: '14px', borderLeft: '3px solid #78A18E' }}>
                "{cat.verse_text}" — {cat.verse_reference}
              </div>
            )}
            <div className="mt-3 text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
              Icon: {cat.icon || 'Default'} • URL: /category/{cat.id}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function FeelingsTab({ feelings, openFeelingForm, deleteFeeling }) {
  const [notifyFor, setNotifyFor] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [loadingPreview, setLoadingPreview] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [channels, setChannels] = React.useState({ send_email: true, send_push: false });

  const openNotify = async (f) => {
    setNotifyFor(f);
    setPreview(null);
    try {
      setLoadingPreview(true);
      const res = await api.get(`/admin/feelings/${f.id}/notify/preview`);
      setPreview(res.data);
    } catch (e) {
      toast.error('Could not load preview');
      setNotifyFor(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const confirmSend = async () => {
    if (!notifyFor || !preview) return;
    const total = preview.total_recipients || 0;
    const how = [channels.send_email && `email ${total} people (${preview.member_count} members + ${preview.newsletter_count} subscribers)`, channels.send_push && 'send a push notification'].filter(Boolean).join(' and ');
    if (!window.confirm(`Send this feelings guidance — ${how}?`)) return;
    try {
      setSending(true);
      const res = await api.post(`/admin/feelings/${notifyFor.id}/notify/send`, channels);
      const { sent, failed, total_recipients, push } = res.data;
      toast.success(`${channels.send_email ? `Emailed ${sent} of ${total_recipients}` : 'Sent'}${describePushResult(push)}`);
      if (failed > 0) toast.error(`${failed} failed to send`);
      setNotifyFor(null);
      setPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Feelings Guidance</h2>
          <p className="text-sm mt-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Guidance shown when someone selects a feeling and who it's about</p>
        </div>
        <button onClick={() => openFeelingForm()} className="flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 hover:-translate-y-1" style={btnStyle('#E57A44', '#FDFBF7')} data-testid="new-feeling-btn">
          <Plus className="w-5 h-5" /><span>New Guidance</span>
        </button>
      </div>
      <div className="space-y-4">
        {feelings.map((f) => (
          <div key={f.id} className="p-6 rounded-2xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)' }}>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex items-center gap-3 flex-wrap">
                <Heart className="w-5 h-5" style={{ color: '#E57A44' }} />
                <span className="font-semibold capitalize" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{f.feeling}</span>
                <span style={{ color: '#8C867D' }}>about</span>
                <span className="font-semibold capitalize" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{f.who_about}</span>
                {f.topic && <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ backgroundColor: '#E57A4418', color: '#C2582A', fontFamily: 'Manrope, sans-serif' }} data-testid={`admin-feeling-topic-${f.id}`}>{f.topic}</span>}
                {f.notified_at && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs" style={{ backgroundColor: '#DFEAE1', color: '#3F5641', fontFamily: 'Manrope, sans-serif', fontWeight: 600 }} title={`Last sent ${new Date(f.notified_at).toLocaleString()}`}>
                    <Check className="w-3 h-3" /> sent
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => openNotify(f)} className="inline-flex items-center gap-1 px-3 py-2 rounded-full transition-all duration-300 hover:-translate-y-0.5" style={{ background: 'linear-gradient(135deg, #F4D67A 0%, #E8C265 55%, #C99B2E 100%)', color: '#3D2E0A', fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 12, boxShadow: '0 3px 10px rgba(201,155,46,0.3)' }} aria-label="Send to members" data-testid={`notify-feeling-${f.id}`}>
                  <Send className="w-3.5 h-3.5" /> Send to members
                </button>
                <button onClick={() => openFeelingForm(f)} className="p-2 rounded-full transition-all duration-300 hover:-translate-y-0.5" style={{ backgroundColor: '#78A18E15', color: '#78A18E' }} aria-label="Edit" data-testid={`edit-feeling-${f.id}`}>
                  <Edit className="w-4 h-4" />
                </button>
                <button onClick={() => deleteFeeling(f.id)} className="p-2 rounded-full transition-all duration-300 hover:-translate-y-0.5" style={{ backgroundColor: '#E57A4415', color: '#E57A44' }} aria-label="Delete" data-testid={`delete-feeling-${f.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>{(f.guidance || '').slice(0, 200)}{(f.guidance || '').length > 200 ? '…' : ''}</p>
            {f.scriptures && f.scriptures.length > 0 && (
              <p className="text-xs mt-3" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                {f.scriptures.length} scripture{f.scriptures.length !== 1 ? 's' : ''} attached
              </p>
            )}
          </div>
        ))}
        {feelings.length === 0 && (
          <p className="text-center py-12" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>No guidance entries yet. Add guidance for feelings and situations.</p>
        )}
      </div>

      {notifyFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(44,42,41,0.5)', backdropFilter: 'blur(4px)' }} onClick={() => !sending && setNotifyFor(null)} data-testid="feelings-notify-modal">
          <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl" style={{ backgroundColor: '#FDFBF7', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center justify-between p-4 border-b" style={{ backgroundColor: '#FDFBF7', borderColor: '#F0EBE0' }}>
              <div>
                <p className="text-xs uppercase tracking-widest" style={{ color: '#8C867D', fontWeight: 700, fontFamily: 'Manrope, sans-serif', letterSpacing: '0.1em' }}>Send Feelings Guidance</p>
                <p className="text-sm font-bold capitalize" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                  {notifyFor.feeling} · about {notifyFor.who_about}
                </p>
              </div>
              <button onClick={() => !sending && setNotifyFor(null)} className="p-2 rounded-full hover:bg-[#F3EFE7]" aria-label="Close" data-testid="feelings-notify-close">
                <AlertTriangle className="w-0" />
                <span style={{ fontSize: 18, fontWeight: 700, color: '#595553' }}>×</span>
              </button>
            </div>
            {loadingPreview && <p className="p-10 text-center" style={{ color: '#8C867D' }}>Loading preview…</p>}
            {preview && (
              <>
                <div className="p-4 flex items-center justify-between flex-wrap gap-3 border-b" style={{ backgroundColor: '#FBEFCE50', borderColor: '#F0EBE0' }}>
                  <div className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#5C4515' }}>
                    Will send to <strong>{preview.total_recipients}</strong> people — {preview.member_count} members + {preview.newsletter_count} newsletter subscribers.
                    {preview.already_notified && (
                      <span className="block text-xs mt-1" style={{ color: '#8C867D' }}>Note: this guidance has been sent before. Sending again will deliver it to everyone.</span>
                    )}
                    <div className="mt-3"><ChannelToggles channels={channels} onChange={setChannels} testidPrefix="feelings-notify-channel" /></div>
                  </div>
                  <button onClick={confirmSend} disabled={sending || (!channels.send_email && !channels.send_push) || (channels.send_email && !channels.send_push && preview.total_recipients === 0)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition disabled:opacity-50 hover:-translate-y-0.5" style={{ background: 'linear-gradient(135deg, #F4D67A 0%, #E8C265 55%, #C99B2E 100%)', color: '#3D2E0A', fontFamily: 'Manrope, sans-serif', boxShadow: '0 4px 14px rgba(201,155,46,0.35)' }} data-testid="feelings-notify-send">
                    <Send className="w-4 h-4" /> {sending ? 'Sending…' : (channels.send_email ? `Send to ${preview.total_recipients}` : 'Send push')}
                  </button>
                </div>
                <div dangerouslySetInnerHTML={{ __html: preview.html }} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function ConsultationsTab({ consultations }) {
  return (
    <div>
      <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>All Consultations</h2>
      <div className="space-y-4">
        {consultations.map((c) => (
          <Link key={c.id} to={`/consultation/${c.id}`} className="block p-6 rounded-2xl transition-all duration-300 hover:-translate-y-1" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)' }}>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="font-semibold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{c.title}</h3>
                <p className="text-sm mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>{c.description.slice(0, 150)}...</p>
                <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{c.user_name} • {c.tier} • {new Date(c.created_at).toLocaleDateString()}</p>
              </div>
              <span className="px-4 py-2 rounded-full text-xs font-semibold uppercase" style={{ backgroundColor: c.status === 'completed' ? '#78A18E20' : c.status === 'pending' ? '#E8C26520' : c.status === 'refunded' ? '#8C867D20' : '#5B7C9920', color: c.status === 'completed' ? '#78A18E' : c.status === 'pending' ? '#E8C265' : c.status === 'refunded' ? '#8C867D' : '#5B7C99', fontFamily: 'Nunito, sans-serif' }}>{c.status.replace('_', ' ')}</span>
            </div>
          </Link>
        ))}
        {consultations.length === 0 && <p className="text-center py-12" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>No consultations yet</p>}
      </div>
    </div>
  );
}

export function PrayerDigestTab({ digestPreview, digestLoading, digestSending, loadDigestPreview, sendDigest }) {
  return (
    <div data-testid="digest-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Weekly Prayer Digest</h2>
          <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
            Send a curated email of top-prayed and newest prayer requests from the last 7 days to all subscribed members.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadDigestPreview} disabled={digestLoading} className="flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 hover:-translate-y-1 disabled:opacity-50" style={{ ...btnStyle('#FDFBF7', '#595553'), border: '1px solid #E6E2D8' }} data-testid="digest-refresh-btn">
            <Mail className="w-4 h-4" /><span>{digestLoading ? 'Refreshing…' : 'Refresh Preview'}</span>
          </button>
          <button onClick={sendDigest} disabled={digestSending || !digestPreview || (digestPreview.top_prayed.length === 0 && digestPreview.newest.length === 0)} className="flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0" style={btnStyle('#E57A44', '#FDFBF7')} data-testid="digest-send-btn">
            <Send className="w-4 h-4" /><span>{digestSending ? 'Sending…' : 'Send Digest Now'}</span>
          </button>
        </div>
      </div>

      {digestLoading && !digestPreview && (
        <p className="py-12 text-center" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Loading preview…</p>
      )}

      {digestPreview && (
        <>
          <div className="p-4 rounded-2xl mb-6 flex items-center gap-3" style={{ backgroundColor: '#78A18E15' }} data-testid="digest-recipient-count">
            <Users className="w-5 h-5" style={{ color: '#78A18E' }} />
            <p style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}>
              <strong>{digestPreview.recipient_count}</strong> subscribed {digestPreview.recipient_count === 1 ? 'member' : 'members'} will receive this digest.
            </p>
          </div>

          {digestPreview.top_prayed.length === 0 && digestPreview.newest.length === 0 ? (
            <div className="p-8 rounded-2xl text-center" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} data-testid="digest-empty-state">
              <Heart className="w-10 h-10 mx-auto mb-3" style={{ color: '#E57A44' }} />
              <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
                No prayer requests in the past 7 days yet. Once a few come in, you'll be able to send a digest here.
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {digestPreview.top_prayed.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold mb-3" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Most Prayed For</h3>
                  <div className="space-y-3" data-testid="digest-top-prayed">
                    {digestPreview.top_prayed.map((p) => (
                      <div key={p.id} className="p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: '#E57A4420', color: '#E57A44', fontFamily: 'Nunito, sans-serif' }}>Most Prayed</span>
                          <span className="text-xs flex items-center gap-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                            <Heart className="w-3 h-3" />{p.prayed_count}
                          </span>
                        </div>
                        <p className="mb-1" style={{ fontFamily: 'Georgia, serif', color: '#2C2A29', lineHeight: 1.7 }}>{p.content}</p>
                        <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>— {p.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {digestPreview.newest.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold mb-3" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>New This Week</h3>
                  <div className="space-y-3" data-testid="digest-newest">
                    {digestPreview.newest.map((p) => (
                      <div key={p.id} className="p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: '#78A18E20', color: '#78A18E', fontFamily: 'Nunito, sans-serif' }}>Newest</span>
                          <span className="text-xs flex items-center gap-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                            <Heart className="w-3 h-3" />{p.prayed_count}
                          </span>
                        </div>
                        <p className="mb-1" style={{ fontFamily: 'Georgia, serif', color: '#2C2A29', lineHeight: 1.7 }}>{p.content}</p>
                        <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>— {p.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function VideoTab({ introVideo, videoUploading, videoProgress, transcriptDraft, setTranscriptDraft, savingTranscript, uploadIntroVideo, saveTranscript, removeIntroVideo }) {
  return (
    <div data-testid="video-panel">
      <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Introduction Video</h2>
      <p className="text-sm mb-6" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
        Upload a welcome video that plays on the home page to greet visitors. MP4, MOV, or WebM. Max 500MB. Only one intro video is active at a time.
      </p>

      {introVideo ? (
        <div className="p-6 rounded-3xl mb-6" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} data-testid="video-current">
          <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
            <div>
              <h3 className="font-semibold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Current Intro Video</h3>
              <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                {introVideo.original_filename} • {(introVideo.size / (1024 * 1024)).toFixed(1)} MB
              </p>
            </div>
            <button onClick={removeIntroVideo} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm" style={{ ...btnStyle('#E57A4415', '#E57A44'), fontSize: '14px' }} data-testid="video-remove-btn">
              <Trash2 className="w-4 h-4" /> Remove
            </button>
          </div>
          <video controls preload="metadata" className="w-full rounded-2xl" style={{ maxHeight: '400px', backgroundColor: '#000' }} data-testid="video-preview-player">
            <source src={`${process.env.REACT_APP_BACKEND_URL}/api/intro-video/stream`} type={introVideo.content_type || 'video/mp4'} />
          </video>
        </div>
      ) : (
        <div className="p-8 rounded-3xl mb-6 text-center" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} data-testid="video-empty">
          <Video className="w-12 h-12 mx-auto mb-3" style={{ color: '#E57A44' }} />
          <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>No intro video yet. Upload one below.</p>
        </div>
      )}

      <label className="block p-8 rounded-3xl text-center cursor-pointer transition-all duration-300 hover:-translate-y-1" style={{ backgroundColor: '#F3EFE7', border: '2px dashed #E57A44' }} data-testid="video-upload-dropzone">
        <Upload className="w-8 h-8 mx-auto mb-3" style={{ color: '#E57A44' }} />
        <p className="font-semibold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
          {videoUploading ? `Uploading… ${videoProgress}%` : (introVideo ? 'Replace intro video' : 'Upload intro video')}
        </p>
        <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Click here or drag a video file (MP4, MOV, WebM — up to 500MB)</p>
        <input type="file" accept="video/mp4,video/quicktime,video/webm,video/x-m4v,video/ogg" className="hidden" disabled={videoUploading} onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadIntroVideo(f); e.target.value = ''; }} data-testid="video-upload-input" />
        {videoUploading && (
          <div className="mt-4 w-full rounded-full h-2 overflow-hidden" style={{ backgroundColor: '#E6E2D8' }}>
            <div style={{ width: `${videoProgress}%`, backgroundColor: '#E57A44', height: '100%', transition: 'width 200ms' }} />
          </div>
        )}
      </label>

      {introVideo && (
        <div className="mt-8 p-6 rounded-3xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} data-testid="video-transcript-editor">
          <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
            <div>
              <h3 className="font-semibold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Video Transcript</h3>
              <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
                Paste a written version of your video message. Visitors — including deaf or hard-of-hearing women — will see a "Read Transcript" panel below the video.
              </p>
            </div>
            <button onClick={saveTranscript} disabled={savingTranscript} className="px-5 py-2 rounded-full disabled:opacity-50" style={{ ...btnStyle('#78A18E', '#FDFBF7'), fontSize: '14px' }} data-testid="video-transcript-save">
              {savingTranscript ? 'Saving…' : 'Save Transcript'}
            </button>
          </div>
          <textarea value={transcriptDraft} onChange={(e) => setTranscriptDraft(e.target.value)} rows={8} maxLength={20000} placeholder="Welcome, dear sister. My name is Genesis, and I made this space for women like you…" className="w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2" style={{ fontFamily: 'Lora, serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2A29', lineHeight: 1.7 }} data-testid="video-transcript-textarea" />
          <p className="text-xs mt-1 text-right" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{transcriptDraft.length} / 20,000</p>
        </div>
      )}
    </div>
  );
}

export function ScriptureLibraryTab({ scriptures, openScriptureForm, deleteScripture }) {
  return (
    <div data-testid="library-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Scripture Library</h2>
          <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Save your favorite verses once, then drop them into new Feelings Guidance with one click.</p>
        </div>
        <button onClick={() => openScriptureForm()} className="flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 hover:-translate-y-1" style={btnStyle('#E57A44', '#FDFBF7')} data-testid="new-scripture-btn">
          <Plus className="w-5 h-5" /><span>Add Scripture</span>
        </button>
      </div>

      {scriptures.length === 0 ? (
        <div className="p-8 rounded-3xl text-center" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} data-testid="library-empty">
          <BookOpen className="w-10 h-10 mx-auto mb-3" style={{ color: '#E57A44' }} />
          <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Your library is empty. Add a scripture to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {scriptures.map((s) => (
            <div key={s.id} className="p-6 rounded-2xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} data-testid={`scripture-${s.id}`}>
              <p className="italic mb-3" style={{ fontFamily: 'Lora, serif', color: '#2C2A29', lineHeight: 1.7 }}>"{s.text}"</p>
              <p className="text-sm font-semibold mb-3" style={{ fontFamily: 'Nunito, sans-serif', color: '#78A18E' }}>— {s.reference}{s.tag ? ` · ${s.tag}` : ''}</p>
              <div className="flex items-center gap-2">
                <button onClick={() => openScriptureForm(s)} className="p-2 rounded-full" style={{ backgroundColor: '#78A18E15', color: '#78A18E' }} aria-label="Edit" data-testid={`edit-scripture-${s.id}`}>
                  <Edit className="w-4 h-4" />
                </button>
                <button onClick={() => deleteScripture(s.id)} className="p-2 rounded-full" style={{ backgroundColor: '#E57A4415', color: '#E57A44' }} aria-label="Delete" data-testid={`delete-scripture-${s.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ContactMessagesTab({ contactMessages, loadContactMessages, markContactRead, deleteContactMessage }) {
  const [openReplyId, setOpenReplyId] = React.useState(null);
  const [replyBody, setReplyBody] = React.useState('');
  const [sending, setSending] = React.useState(false);

  const openReply = (m) => {
    setOpenReplyId(m.id);
    setReplyBody(`Hi ${(m.name || '').split(' ')[0] || 'friend'},\n\n`);
  };

  const cancelReply = () => { setOpenReplyId(null); setReplyBody(''); };

  const sendReply = async (mid) => {
    if (!replyBody.trim()) { toast.error('Write a reply first'); return; }
    setSending(true);
    try {
      await api.post(`/admin/contact-messages/${mid}/reply`, { body: replyBody.trim() });
      toast.success('Reply sent from Love Restored');
      cancelReply();
      loadContactMessages();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send reply');
    } finally { setSending(false); }
  };

  return (
    <div data-testid="messages-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Contact Messages</h2>
          <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Messages sent through the Contact form. Reply directly here — sent as <strong>Love Restored &lt;hello@loverestored.net&gt;</strong>.</p>
        </div>
        <button onClick={loadContactMessages} className="px-4 py-2 rounded-full text-sm" style={{ ...btnStyle('#FDFBF7', '#595553'), border: '1px solid #E6E2D8' }} data-testid="messages-refresh-btn">Refresh</button>
      </div>

      {contactMessages.length === 0 ? (
        <div className="p-8 rounded-3xl text-center" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} data-testid="messages-empty">
          <Mail className="w-10 h-10 mx-auto mb-3" style={{ color: '#E57A44' }} />
          <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>No messages yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {contactMessages.map((m) => (
            <div key={m.id} className="p-6 rounded-2xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', borderLeft: m.is_read ? '4px solid transparent' : '4px solid #E57A44' }} data-testid={`message-${m.id}`}>
              <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                <div>
                  <p className="font-semibold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{m.name} <span style={{ color: '#8C867D', fontWeight: 400 }}>&lt;{m.email}&gt;</span></p>
                  <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{new Date(m.created_at).toLocaleString()}</p>
                </div>
                {!m.is_read && <span className="px-3 py-1 rounded-full text-xs font-semibold uppercase" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#E57A4420', color: '#E57A44' }}>New</span>}
              </div>
              <p className="mb-4" style={{ fontFamily: 'Lora, serif', color: '#2C2A29', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{m.message}</p>

              {/* Previous replies thread */}
              {(m.replies || []).length > 0 && (
                <div className="mb-4 pl-4 space-y-3" style={{ borderLeft: '2px solid #78A18E30' }} data-testid={`message-${m.id}-replies`}>
                  {m.replies.map((r) => (
                    <div key={r.id} className="p-3 rounded-xl" style={{ backgroundColor: '#F8FBF9' }} data-testid={`reply-entry-${r.id}`}>
                      <div className="flex items-center gap-2 mb-1 text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                        <span style={{ color: '#5C715E', fontWeight: 700 }}>You replied</span>
                        <span>· {new Date(r.sent_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap" style={{ fontFamily: 'Lora, serif', color: '#2C2A29', lineHeight: 1.6 }}>{r.body}</p>
                    </div>
                  ))}
                </div>
              )}

              {openReplyId === m.id ? (
                <div className="mt-4 p-4 rounded-2xl" style={{ backgroundColor: '#FFF5E5', border: '1px solid #E8C26555' }} data-testid={`reply-form-${m.id}`}>
                  <p className="text-xs mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                    Sending as <strong>Love Restored &lt;hello@loverestored.net&gt;</strong> → {m.email}
                  </p>
                  <textarea
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    rows={7}
                    maxLength={4000}
                    placeholder="Write a warm, personal reply…"
                    className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2"
                    style={{ fontFamily: 'Lora, serif', backgroundColor: '#FFFFFF', borderColor: '#E6E2D8', color: '#2C2724', lineHeight: 1.6 }}
                    data-testid={`reply-textarea-${m.id}`}
                  />
                  <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                    <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{replyBody.length} / 4000</p>
                    <div className="flex items-center gap-2">
                      <button onClick={cancelReply} disabled={sending}
                        className="px-4 py-2 rounded-full text-sm"
                        style={{ ...btnStyle('#FDFBF7', '#595553'), border: '1px solid #E6E2D8' }}
                        data-testid={`reply-cancel-${m.id}`}>
                        Cancel
                      </button>
                      <button onClick={() => sendReply(m.id)} disabled={sending || !replyBody.trim()}
                        className="flex items-center gap-2 px-5 py-2 rounded-full text-sm disabled:opacity-50"
                        style={btnStyle('#E57A44', '#FDFBF7')}
                        data-testid={`reply-send-${m.id}`}>
                        <Send className="w-4 h-4" /> {sending ? 'Sending…' : 'Send from Love Restored'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => openReply(m)}
                    className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition hover:-translate-y-0.5"
                    style={btnStyle('#E57A44', '#FDFBF7')}
                    data-testid={`reply-message-${m.id}`}>
                    <Send className="w-4 h-4" /> {(m.replies || []).length > 0 ? 'Reply again' : 'Reply from Love Restored'}
                  </button>
                  {!m.is_read && (
                    <button onClick={() => markContactRead(m.id)} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm" style={btnStyle('#78A18E20', '#78A18E')} data-testid={`mark-read-${m.id}`}>
                      <Check className="w-4 h-4" /> Mark Read
                    </button>
                  )}
                  <button onClick={() => deleteContactMessage(m.id)} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm" style={btnStyle('#E57A4415', '#E57A44')} data-testid={`delete-message-${m.id}`}>
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FAQsTab({ faqs, openFaqForm, deleteFaq }) {
  return (
    <div data-testid="faqs-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Frequently Asked Questions</h2>
          <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Add answers to common questions. They appear at <strong>loverestored.net/faq</strong>.</p>
        </div>
        <button onClick={() => openFaqForm()} className="flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 hover:-translate-y-1" style={btnStyle('#E57A44', '#FDFBF7')} data-testid="new-faq-btn">
          <Plus className="w-5 h-5" /><span>Add FAQ</span>
        </button>
      </div>
      {faqs.length === 0 ? (
        <div className="p-8 rounded-3xl text-center" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} data-testid="faqs-empty">
          <HelpCircle className="w-10 h-10 mx-auto mb-3" style={{ color: '#E57A44' }} />
          <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>No FAQs yet. Add one to help visitors.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {faqs.map((f) => (
            <div key={f.id} className="p-6 rounded-2xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} data-testid={`faq-${f.id}`}>
              <div className="flex items-start justify-between gap-4 mb-2">
                <h3 className="font-semibold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{f.question}</h3>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => openFaqForm(f)} className="p-2 rounded-full" style={{ backgroundColor: '#78A18E15', color: '#78A18E' }} aria-label="Edit" data-testid={`edit-faq-${f.id}`}><Edit className="w-4 h-4" /></button>
                  <button onClick={() => deleteFaq(f.id)} className="p-2 rounded-full" style={{ backgroundColor: '#E57A4415', color: '#E57A44' }} aria-label="Delete" data-testid={`delete-faq-${f.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="text-sm" style={{ fontFamily: 'Lora, serif', color: '#595553', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{f.answer}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function TopicsTab({ adminTopics, updateTopicStatus, removeTopic }) {
  return (
    <div data-testid="topics-panel">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Topic Ideas from the Community</h2>
        <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>What visitors want you to write about — sorted by their upvotes.</p>
      </div>
      {adminTopics.length === 0 ? (
        <div className="p-8 rounded-3xl text-center" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} data-testid="topics-admin-empty">
          <Lightbulb className="w-10 h-10 mx-auto mb-3" style={{ color: '#E57A44' }} />
          <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>No suggestions yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {adminTopics.map((t) => (
            <div key={t.id} className="p-5 rounded-2xl flex items-start gap-4" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} data-testid={`admin-topic-${t.id}`}>
              <div className="flex flex-col items-center justify-center rounded-2xl p-3 min-w-[64px]" style={{ backgroundColor: '#F3EFE7' }}>
                <ChevronUp className="w-5 h-5" strokeWidth={2.5} style={{ color: '#E57A44' }} />
                <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, color: '#2C2A29' }}>{t.upvote_count}</span>
              </div>
              <div className="flex-1">
                <p className="font-semibold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{t.title}</p>
                {t.description && <p className="text-sm mt-1 mb-2" style={{ fontFamily: 'Lora, serif', color: '#595553', lineHeight: 1.6 }}>{t.description}</p>}
                <p className="text-xs mb-3" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>— {t.author_name} · {new Date(t.created_at).toLocaleDateString()}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <select value={t.status} onChange={(e) => updateTopicStatus(t.id, e.target.value)} className="text-xs px-3 py-1 rounded-full" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#F3EFE7', color: '#595553', fontWeight: 600, border: 'none' }} data-testid={`topic-status-${t.id}`}>
                    <option value="new">New</option>
                    <option value="considering">Considering</option>
                    <option value="written">Written</option>
                    <option value="declined">Declined</option>
                  </select>
                  <button onClick={() => removeTopic(t.id)} className="p-1.5 rounded-full" style={{ backgroundColor: '#E57A4415', color: '#E57A44' }} aria-label="Delete" data-testid={`delete-topic-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function EngagementTab({ digestDays, setDigestDays, loadEngagementDigestPreview, digestPreviewData, digestSendingComments, sendEngagementDigest }) {
  return (
    <div data-testid="engagement-panel">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Community Engagement Digest</h2>
        <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>See new reactions and encouragement comments — and email yourself a summary.</p>
      </div>
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <label className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Last</label>
        <select value={digestDays} onChange={(e) => { const d = Number(e.target.value); setDigestDays(d); loadEngagementDigestPreview(d); }} className="px-3 py-1.5 rounded-full text-sm" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#F3EFE7', color: '#2C2A29', fontWeight: 600, border: 'none' }} data-testid="digest-days-select">
          <option value={1}>1 day</option>
          <option value={7}>7 days</option>
          <option value={30}>30 days</option>
        </select>
        <button onClick={sendEngagementDigest} disabled={digestSendingComments} className="flex items-center gap-2 px-5 py-2 rounded-full disabled:opacity-50" style={btnStyle('#E57A44', '#FDFBF7')} data-testid="digest-send-comments">
          <Send className="w-4 h-4" /><span>{digestSendingComments ? 'Sending…' : 'Email me the digest'}</span>
        </button>
      </div>
      {digestPreviewData && (
        <>
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <div className="p-4 rounded-2xl flex-1 min-w-[140px]" style={{ backgroundColor: '#E57A4415' }}>
              <p className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#E57A44' }}>{digestPreviewData.comment_count}</p>
              <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>encouragement comments</p>
            </div>
            <div className="p-4 rounded-2xl flex-1 min-w-[140px]" style={{ backgroundColor: '#78A18E15' }}>
              <p className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#78A18E' }}>{digestPreviewData.reaction_count}</p>
              <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>reactions</p>
            </div>
          </div>
          {digestPreviewData.comments.length === 0 ? (
            <div className="p-8 rounded-3xl text-center" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} data-testid="engagement-empty">
              <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>No new comments in this window.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {digestPreviewData.comments.map((c) => (
                <div key={c.id} className="p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', borderLeft: c.parent_type === 'prayer' ? '3px solid #78A18E' : '3px solid #5B7C99' }}>
                  <p className="text-xs mb-1 uppercase tracking-wide" style={{ fontFamily: 'Nunito, sans-serif', color: c.parent_type === 'prayer' ? '#78A18E' : '#5B7C99', fontWeight: 700 }}>{c.parent_type}</p>
                  {c.parent_snippet && <p className="text-xs italic mb-2" style={{ fontFamily: 'Lora, serif', color: '#8C867D' }}>on: "{c.parent_snippet}…"</p>}
                  <p style={{ fontFamily: 'Lora, serif', color: '#2C2A29', lineHeight: 1.6 }}>{c.content}</p>
                  <p className="text-xs mt-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>— {c.display_name} · {new Date(c.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function StoriesTab({ adminTestimonials, loadAdminTestimonials, approveTestimonial, deleteTestimonial }) {
  return (
    <div data-testid="stories-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Testimonials</h2>
          <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
            Review restoration stories submitted by visitors. Approved stories appear on the public Testimonials page and Home page.
          </p>
        </div>
        <button onClick={loadAdminTestimonials} className="px-4 py-2 rounded-full text-sm" style={{ ...btnStyle('#FDFBF7', '#595553'), border: '1px solid #E6E2D8' }} data-testid="stories-refresh-btn">Refresh</button>
      </div>

      {adminTestimonials.length === 0 ? (
        <div className="p-8 rounded-3xl text-center" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} data-testid="stories-empty">
          <Quote className="w-10 h-10 mx-auto mb-3" style={{ color: '#E57A44' }} />
          <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>No testimonials yet. Once visitors share their stories, they'll appear here for approval.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {adminTestimonials.map((t) => (
            <div key={t.id} className="p-6 rounded-2xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} data-testid={`admin-testimonial-${t.id}`}>
              <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                <div>
                  <p className="font-semibold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                    {t.name}{t.partner_name ? ` & ${t.partner_name}` : ''}
                  </p>
                  <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                    Submitted {new Date(t.created_at).toLocaleDateString()}
                  </p>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-semibold uppercase" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: t.status === 'approved' ? '#78A18E20' : '#E8C26520', color: t.status === 'approved' ? '#78A18E' : '#E8C265' }}>{t.status}</span>
              </div>
              <p className="mb-4" style={{ fontFamily: 'Lora, serif', color: '#2C2A29', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{t.story}</p>
              <div className="flex items-center gap-2">
                {t.status !== 'approved' && (
                  <button onClick={() => approveTestimonial(t.id)} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm" style={btnStyle('#78A18E', '#FDFBF7')} data-testid={`approve-testimonial-${t.id}`}>
                    <Check className="w-4 h-4" /> Approve
                  </button>
                )}
                <button onClick={() => deleteTestimonial(t.id)} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm" style={btnStyle('#E57A4415', '#E57A44')} data-testid={`delete-testimonial-${t.id}`}>
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



// Admin-only email-free password reset. Useful when Resend/DNS is having issues
// or when a user says "I never got the email". Type an email, get a clickable
// reset link on-screen, copy it, hand it to the user via text/DM/in-person.
function RevealResetLinkPanel() {
  const [email, setEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const handleReveal = async (e) => {
    e && e.preventDefault && e.preventDefault();
    if (!email.trim()) { toast.error('Enter an email first'); return; }
    setLoading(true); setError(''); setResult(null);
    try {
      const { data } = await api.post('/auth/admin/reveal-reset-link', {
        email: email.trim(),
        origin_url: window.location.origin,
      });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not generate reset link');
    } finally { setLoading(false); }
  };

  const copyLink = async () => {
    if (!result?.reset_url) return;
    try {
      await navigator.clipboard.writeText(result.reset_url);
      toast.success('Reset link copied — paste it into a text or DM to your user.');
    } catch {
      toast.error('Copy failed — long-press the link to copy manually.');
    }
  };

  return (
    <div className="p-4 mb-4 rounded-2xl" style={{ backgroundColor: '#FFF7ED', border: '1px solid #FDBA7440' }} data-testid="reveal-reset-panel">
      <div className="flex items-center gap-2 mb-2">
        <Key className="w-4 h-4" style={{ color: '#E57A44' }} />
        <span className="text-sm font-semibold" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}>Reveal password reset link</span>
      </div>
      <p className="text-xs mb-3" style={{ fontFamily: 'Nunito, sans-serif', color: '#7A5C3C' }}>
        When a user says "I never got the reset email", type their email below to get their reset link directly. Share it with them personally via text, DM, or in person. Link expires in 1 hour.
      </p>

      <form onSubmit={handleReveal} className="flex flex-wrap items-center gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@example.com"
          className="flex-1 min-w-[220px] px-3 py-1.5 rounded-full text-sm"
          style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FFFFFF', border: '1px solid #E6E2D8', color: '#2C2A29' }}
          data-testid="reveal-reset-email"
        />
        <button
          type="submit"
          disabled={loading || !email.trim()}
          className="px-4 py-1.5 rounded-full text-xs font-semibold transition"
          style={{ backgroundColor: '#E57A44', color: '#FDFBF7', fontFamily: 'Nunito, sans-serif', opacity: (loading || !email.trim()) ? 0.5 : 1 }}
          data-testid="reveal-reset-submit"
        >
          {loading ? 'Generating…' : 'Reveal reset link'}
        </button>
      </form>

      {error && (
        <div className="mt-3 p-3 rounded-xl flex items-start gap-2" style={{ backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5' }} data-testid="reveal-reset-error">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#991B1B' }} />
          <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#991B1B' }}>{error}</p>
        </div>
      )}

      {result && !result.exists && (
        <div className="mt-3 p-3 rounded-xl" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' }} data-testid="reveal-reset-no-user">
          <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#78350F' }}>
            <strong>No account</strong> for <strong>{result.email}</strong>. Tell them to <em>register first</em> at /register, then you can reset their password.
          </p>
        </div>
      )}

      {result && result.exists && result.reset_url && (
        <div className="mt-3 p-3 rounded-xl space-y-2" style={{ backgroundColor: '#F0F9F4', border: '1px solid #78A18E40' }} data-testid="reveal-reset-success">
          <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#365B48' }}>
            Reset link for <strong>{result.name || result.email}</strong> ({result.email}) — valid for 1 hour:
          </p>
          <div className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: '#FFFFFF', border: '1px solid #78A18E30' }}>
            <code className="flex-1 text-xs break-all" style={{ fontFamily: 'monospace', color: '#2C2A29' }} data-testid="reveal-reset-url">
              {result.reset_url}
            </code>
            <button
              onClick={copyLink}
              className="px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1 flex-shrink-0"
              style={{ backgroundColor: '#78A18E', color: '#FFFFFF', fontFamily: 'Nunito, sans-serif' }}
              data-testid="reveal-reset-copy"
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
          <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#4B6B5C' }}>
            Send this link to the user privately (text, DM, in person). When they tap it, they'll be able to set a new password.
          </p>
        </div>
      )}
    </div>
  );
}


export function MembersTab({ members, loadMembers, deleteMember, bulkDeleteMembers, sendWelcomeToAll, sendWelcomeToSelected, sendTestEmail }) {
  const [selected, setSelected] = React.useState(new Set());
  const [selectMode, setSelectMode] = React.useState(null); // null | 'delete' | 'welcome'
  const [testEmail, setTestEmail] = React.useState('');
  const [testSending, setTestSending] = React.useState('');
  const totalSubscribed = members.filter(m => m.email_subscribed !== false).length;
  const admins = members.filter(m => m.role === 'admin').length;
  const neverReceivedWelcome = members.filter(m => m.role !== 'admin' && !m.welcome_sent).length;
  const nonAdminMembers = members.filter(m => m.role !== 'admin');
  const allNonAdminSelected = nonAdminMembers.length > 0 && nonAdminMembers.every(m => selected.has(m.id));

  const handleTest = async (kind) => {
    if (!testEmail.trim()) { toast.error('Please enter an email first'); return; }
    setTestSending(kind);
    try {
      const ok = await sendTestEmail(testEmail.trim(), kind);
      if (ok) toast.success(`Test ${kind === 'welcome' ? 'welcome' : 'article'} email sent — check ${testEmail.trim()} (and the spam folder)`);
    } finally { setTestSending(''); }
  };

  const handleTestRenewal = async () => {
    if (!testEmail.trim()) { toast.error('Please enter an email first'); return; }
    setTestSending('renewal');
    try {
      const { data } = await api.post('/admin/sanctuary/test-renewal-email', { email: testEmail.trim() });
      if (data.sent) toast.success(`Renewal preview sent — check ${testEmail.trim()}`);
      else toast.error('Email skipped — Resend key missing or domain not verified yet');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send');
    } finally { setTestSending(''); }
  };

  const enterMode = (mode) => { setSelectMode(mode); setSelected(new Set()); };
  const exitMode = () => { setSelectMode(null); setSelected(new Set()); };
  const toggleOne = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (allNonAdminSelected) setSelected(new Set());
    else setSelected(new Set(nonAdminMembers.map(m => m.id)));
  };
  const handleBulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const ok = await bulkDeleteMembers(ids);
    if (ok) exitMode();
  };
  const handleBulkWelcome = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const ok = await sendWelcomeToSelected(ids);
    if (ok) exitMode();
  };

  const isDeleteMode = selectMode === 'delete';
  const isWelcomeMode = selectMode === 'welcome';
  const inSelectMode = !!selectMode;
  const modeTint = isDeleteMode ? '#DC2626' : '#78A18E';

  return (
    <div data-testid="members-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-6 h-6" style={{ color: '#5B7C99' }} />
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Members</h2>
          </div>
          <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
            <strong>{members.length}</strong> total · <strong>{totalSubscribed}</strong> subscribed · <strong>{admins}</strong> admin · <strong>{neverReceivedWelcome}</strong> haven't received a welcome email yet.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={loadMembers} className="px-4 py-2 rounded-full text-sm" style={{ ...btnStyle('#FDFBF7', '#595553'), border: '1px solid #E6E2D8' }} data-testid="members-refresh-btn">Refresh</button>
          {!inSelectMode && (
            <>
              <button
                onClick={() => sendWelcomeToAll && sendWelcomeToAll(neverReceivedWelcome)}
                disabled={neverReceivedWelcome === 0}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition"
                style={{ backgroundColor: neverReceivedWelcome === 0 ? '#F3EFE7' : '#78A18E', color: neverReceivedWelcome === 0 ? '#C9C3B4' : '#FDFBF7', cursor: neverReceivedWelcome === 0 ? 'not-allowed' : 'pointer' }}
                title={neverReceivedWelcome === 0 ? 'Everyone has already received a welcome' : `Send to ${neverReceivedWelcome} members`}
                data-testid="welcome-send-all-btn"
              >
                <Mail className="w-4 h-4" /> Welcome all ({neverReceivedWelcome})
              </button>
              <button
                onClick={() => enterMode('welcome')}
                disabled={nonAdminMembers.length === 0}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition"
                style={{ backgroundColor: '#78A18E15', color: '#4B6B5C', border: '1px solid #78A18E40', opacity: nonAdminMembers.length === 0 ? 0.4 : 1 }}
                data-testid="welcome-enter-select-mode"
              >
                <Mail className="w-4 h-4" /> Welcome selected…
              </button>
              <button
                onClick={() => enterMode('delete')}
                disabled={nonAdminMembers.length === 0}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition"
                style={{ backgroundColor: '#DC262615', color: '#DC2626', border: '1px solid #DC262640', opacity: nonAdminMembers.length === 0 ? 0.4 : 1 }}
                data-testid="members-enter-select-mode"
              >
                <Trash2 className="w-4 h-4" /> Delete members
              </button>
            </>
          )}
          {inSelectMode && (
            <>
              <button
                onClick={exitMode}
                className="px-4 py-2 rounded-full text-sm font-semibold"
                style={{ backgroundColor: '#FDFBF7', color: '#595553', border: '1px solid #E6E2D8' }}
                data-testid="members-cancel-select"
              >
                Cancel
              </button>
              <button
                onClick={isDeleteMode ? handleBulkDelete : handleBulkWelcome}
                disabled={selected.size === 0}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition"
                style={{ backgroundColor: selected.size === 0 ? '#F3EFE7' : modeTint, color: selected.size === 0 ? '#C9C3B4' : '#FDFBF7', cursor: selected.size === 0 ? 'not-allowed' : 'pointer' }}
                data-testid={isDeleteMode ? "members-bulk-delete-btn" : "members-bulk-welcome-btn"}
              >
                {isDeleteMode ? <Trash2 className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                {isDeleteMode ? `Delete selected (${selected.size})` : `Send welcome (${selected.size})`}
              </button>
            </>
          )}
        </div>
      </div>

      {inSelectMode && (
        <div className="p-3 mb-4 rounded-2xl flex items-center gap-3 flex-wrap" style={{ backgroundColor: isDeleteMode ? '#FEF2F2' : '#F0F9F4', border: `1px solid ${modeTint}25` }}>
          <span className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: isDeleteMode ? '#7F1D1D' : '#365B48', fontWeight: 600 }}>
            {isDeleteMode ? 'Select-to-delete mode is ON.' : 'Select members to receive a welcome email.'}
          </span>
          <span className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: isDeleteMode ? '#991B1B' : '#4B6B5C' }}>
            {isDeleteMode
              ? "Tap the boxes next to members you want to remove. Admin accounts are protected."
              : "Tap the boxes next to members. This sends the standard welcome email; you can send it again even if they've received one before."}
          </span>
          <button onClick={toggleAll} className="ml-auto text-xs font-semibold px-3 py-1 rounded-full" style={{ backgroundColor: '#FFFFFF', color: modeTint, border: `1px solid ${modeTint}40`, fontFamily: 'Nunito, sans-serif' }} data-testid="members-toggle-all">
            {allNonAdminSelected ? 'Deselect all' : 'Select all members'}
          </button>
        </div>
      )}

      {/* Send a test email panel */}
      {!inSelectMode && (
        <div className="p-4 mb-4 rounded-2xl flex items-center gap-3 flex-wrap" style={{ backgroundColor: '#F0F5FA', border: '1px solid #5B7C9925' }} data-testid="test-email-panel">
          <div className="flex items-center gap-2 flex-shrink-0">
            <Send className="w-4 h-4" style={{ color: '#5B7C99' }} />
            <span className="text-sm font-semibold" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}>Send a test email</span>
          </div>
          <input
            type="email"
            placeholder="your.email@example.com"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className="flex-1 min-w-[220px] px-3 py-1.5 rounded-full text-sm"
            style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FFFFFF', border: '1px solid #E6E2D8', color: '#2C2A29' }}
            data-testid="test-email-input"
          />
          <button
            onClick={() => handleTest('welcome')}
            disabled={!!testSending || !testEmail.trim()}
            className="px-3 py-1.5 rounded-full text-xs font-semibold transition"
            style={{ backgroundColor: '#78A18E', color: '#FDFBF7', fontFamily: 'Nunito, sans-serif', opacity: (!!testSending || !testEmail.trim()) ? 0.5 : 1 }}
            data-testid="test-welcome-btn"
          >
            {testSending === 'welcome' ? 'Sending…' : 'Test welcome'}
          </button>
          <button
            onClick={() => handleTest('latest_article')}
            disabled={!!testSending || !testEmail.trim()}
            className="px-3 py-1.5 rounded-full text-xs font-semibold transition"
            style={{ backgroundColor: '#E57A44', color: '#FDFBF7', fontFamily: 'Nunito, sans-serif', opacity: (!!testSending || !testEmail.trim()) ? 0.5 : 1 }}
            data-testid="test-article-btn"
          >
            {testSending === 'latest_article' ? 'Sending…' : 'Test latest post'}
          </button>
          <button
            onClick={handleTestRenewal}
            disabled={!!testSending || !testEmail.trim()}
            className="px-3 py-1.5 rounded-full text-xs font-semibold transition"
            style={{ backgroundColor: '#5B7C99', color: '#FDFBF7', fontFamily: 'Nunito, sans-serif', opacity: (!!testSending || !testEmail.trim()) ? 0.5 : 1 }}
            data-testid="test-renewal-btn"
          >
            {testSending === 'renewal' ? 'Sending…' : 'Preview renewal email'}
          </button>
          <span className="w-full text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#5B7C99' }}>
            Use this to check whether real users are actually receiving your emails. Try your own address first, then a friend's from a different provider (Gmail + Yahoo, for example).
          </span>
        </div>
      )}

      {/* Reveal reset link — email-free password reset for users who can't get the email */}
      {!inSelectMode && (
        <RevealResetLinkPanel />
      )}

      {members.length === 0 ? (
        <div className="p-8 rounded-3xl text-center" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} data-testid="members-empty">
          <Users className="w-10 h-10 mx-auto mb-3" style={{ color: '#5B7C99' }} />
          <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>No registered members yet.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
          <table className="w-full text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
            <thead>
              <tr style={{ backgroundColor: '#F3EFE7', color: '#595553' }}>
                {inSelectMode && <th className="text-left px-3 py-3 font-semibold w-10"></th>}
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Email</th>
                <th className="text-left px-4 py-3 font-semibold">Role</th>
                <th className="text-left px-4 py-3 font-semibold">Joined</th>
                <th className="text-left px-4 py-3 font-semibold">Welcome</th>
                <th className="text-left px-4 py-3 font-semibold">Subscribed</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m, i) => {
                const isAdmin = m.role === 'admin';
                const isChecked = selected.has(m.id);
                return (
                  <tr key={m.id} style={{ backgroundColor: isChecked ? (isDeleteMode ? '#FEF2F2' : '#F0F9F4') : i % 2 === 0 ? '#FFFFFF' : '#FDFBF7' }} data-testid={`member-row-${m.id}`}>
                    {inSelectMode && (
                      <td className="px-3 py-3">
                        {isAdmin ? (
                          <span style={{ color: '#C9C3B4' }}>—</span>
                        ) : (
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleOne(m.id)}
                            aria-label={`Select ${m.email}`}
                            className="cursor-pointer w-4 h-4"
                            style={{ accentColor: modeTint }}
                            data-testid={`member-checkbox-${m.id}`}
                          />
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3" style={{ color: '#2C2A29', fontWeight: 500 }}>{m.name || '—'}</td>
                    <td className="px-4 py-3">
                      <a href={`mailto:${m.email}`} style={{ color: '#5B7C99', textDecoration: 'none' }}>{m.email}</a>
                    </td>
                    <td className="px-4 py-3">
                      {isAdmin ? (
                        <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: '#E57A4420', color: '#E57A44' }}>Admin</span>
                      ) : (
                        <span style={{ color: '#8C867D' }}>Member</span>
                      )}
                    </td>
                    <td className="px-4 py-3" style={{ color: '#8C867D' }}>{m.created_at ? new Date(m.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}</td>
                    <td className="px-4 py-3">
                      {m.welcome_sent ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: '#78A18E20', color: '#4B6B5C' }}>
                          <Check className="w-3 h-3" /> Sent
                        </span>
                      ) : (
                        <span style={{ color: '#DC2626', fontSize: 12, fontWeight: 600 }}>Not sent</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {m.email_subscribed === false
                        ? <span style={{ color: '#DC2626' }}>Unsubscribed</span>
                        : <span style={{ color: '#78A18E' }}>Yes</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function VerseOfDayTab({ verseDraft, setVerseDraft, savingVerse, saveVerseOfDay, clearVerseOfDay, activeVerse }) {
  return (
    <div data-testid="verse-panel">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-6 h-6" style={{ color: '#E57A44' }} />
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Verse of the Week</h2>
        </div>
        <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
          One verse to greet everyone who visits your home page. Update it as often as you'd like — weekly, or whenever the Spirit leads you.
        </p>
      </div>

      {activeVerse ? (
        <div className="p-5 rounded-2xl mb-6" style={{ backgroundColor: '#78A18E15', border: '1px solid #78A18E30' }} data-testid="verse-current">
          <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#4B6B5C' }}>Currently showing</p>
          <blockquote className="italic mb-2" style={{ fontFamily: 'Lora, serif', color: '#2C2A29', fontSize: '17px', lineHeight: 1.7 }}>"{activeVerse.text}"</blockquote>
          <p className="text-sm font-semibold" style={{ fontFamily: 'Nunito, sans-serif', color: '#4B6B5C' }}>— {activeVerse.reference}</p>
        </div>
      ) : (
        <div className="p-5 rounded-2xl mb-6 text-center" style={{ backgroundColor: '#F3EFE7' }} data-testid="verse-empty">
          <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>No verse showing right now. Save one below to make it visible on the home page.</p>
        </div>
      )}

      <div className="p-6 rounded-3xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
        <label className="block mb-2 font-semibold text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}>
          Verse Text <span style={{ color: '#DC2626' }}>*</span>
        </label>
        <textarea
          value={verseDraft.text}
          onChange={(e) => setVerseDraft({ ...verseDraft, text: e.target.value })}
          rows={3}
          maxLength={800}
          placeholder="For I know the plans I have for you, declares the Lord…"
          className="w-full px-4 py-3 rounded-xl border"
          style={{ fontFamily: 'Lora, serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2A29', lineHeight: 1.7 }}
          data-testid="verse-text"
        />
        <p className="text-xs mt-1 text-right" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{verseDraft.text.length} / 800</p>

        <label className="block mb-2 mt-4 font-semibold text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}>
          Reference <span style={{ color: '#DC2626' }}>*</span>
        </label>
        <input
          type="text"
          value={verseDraft.reference}
          onChange={(e) => setVerseDraft({ ...verseDraft, reference: e.target.value })}
          placeholder="Jeremiah 29:11"
          className="w-full px-4 py-3 rounded-xl border"
          style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2A29' }}
          data-testid="verse-reference"
        />

        <div className="flex gap-3 mt-6 flex-wrap">
          <button
            onClick={saveVerseOfDay}
            disabled={savingVerse || !verseDraft.text.trim() || !verseDraft.reference.trim()}
            className="flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 disabled:hover:translate-y-0"
            style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}
            data-testid="save-verse"
          >
            <Sparkles className="w-4 h-4" />
            <span>{savingVerse ? 'Saving…' : (activeVerse ? 'Update Verse' : 'Publish Verse')}</span>
          </button>
          {activeVerse && (
            <button
              onClick={clearVerseOfDay}
              className="flex items-center gap-2 px-5 py-3 rounded-full transition-all duration-300 hover:-translate-y-1"
              style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#E57A4415', color: '#E57A44', fontWeight: 600, fontSize: '14px' }}
              data-testid="clear-verse"
            >
              <Trash2 className="w-4 h-4" /> Hide from Home
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

